/* eslint-disable react/require-default-props */
import React, { useCallback, useContext, useMemo, useState } from 'react';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Link,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Dismiss24Regular,
  LockClosed20Regular,
  Mail20Regular,
  PeopleTeam20Regular,
} from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { DrawerStackProvider, StackedDrawer } from '@bingads-webui-campaign-react/drawer-stack';

import { useHelpResourcesDrawerStyles } from './use-help-resources-drawer-styles';
import { HelpResourcesDetailDrawer } from './help-resources-detail-drawer';
import {
  HELP_RESOURCE_CONTENT,
  HELP_RESOURCE_ORDER,
  type HelpResourceOption,
} from './help-resources-content';

export interface HelpResourcesDrawerProps {
  open: boolean;
  onClose: () => void;
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
}

const DRAWER_WIDTH = '560px';
const CONTACT_SUPPORT_URL = 'https://go.microsoft.com/fwlink/?LinkId=398371';

// Icon shown on each option card, keyed by option.
const OPTION_ICONS: Record<HelpResourceOption, React.ReactElement> = {
  newVsExisting: <PeopleTeam20Regular />,
  signInHelp: <LockClosed20Regular />,
  invitationIssues: <Mail20Regular />,
};

// The invitation "Help resources" drawer (Bug 10923920). Opened from the "Get help here." footer
// button on the pre-auth invitation page. Presents three option cards; picking one opens a stacked
// detail drawer (via @bingads-webui-campaign-react/drawer-stack) with guided Q&A content.
export const HelpResourcesDrawer: React.FC<HelpResourcesDrawerProps> = ({ open, onClose, logger }) => {
  const i18n = useContext(I18nContext);
  const styles = useHelpResourcesDrawerStyles();
  const [detailOption, setDetailOption] = useState<HelpResourceOption | null>(null);

  const handleOptionClick = useCallback((option: HelpResourceOption) => {
    logger?.trace(`HelpResourcesDrawer: Selected option=${option}`);
    setDetailOption(option);
  }, [logger]);

  const handleDetailClose = useCallback(() => {
    setDetailOption(null);
  }, []);

  const handleClose = useCallback(() => {
    logger?.trace('HelpResourcesDrawer: Closed main drawer');
    setDetailOption(null);
    onClose();
  }, [onClose, logger]);

  const isDetailOpen = open && !!detailOption;

  const openDrawers = useMemo(() => {
    const drawers: string[] = [];
    if (open) drawers.push('help-resources-main');
    if (isDetailOpen) drawers.push('help-resources-detail');
    return drawers;
  }, [open, isDetailOpen]);

  return (
    <DrawerStackProvider drawerWidth={DRAWER_WIDTH} openDrawers={openDrawers}>
      <StackedDrawer
        className="help-resources-main-drawer"
        name="help-resources-main"
        onOpenChange={(_, { open: isOpen }) => {
          if (!isOpen) {
            handleClose();
          }
        }}
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                aria-label={i18n.getString(_TL_('Close'))}
                icon={<Dismiss24Regular />}
                onClick={handleClose}
              />
            }
          >
            {i18n.getString(_TL_('Help resources'))}
          </DrawerHeaderTitle>
        </DrawerHeader>

        <DrawerBody className={mergeClasses('help-resources-main-body', styles.body)}>
          <div>
            <Body1 className={styles.introText}>
              {i18n.getString(_TL_('Choose what you need help with.'))}
            </Body1>

            <div className={styles.optionCards}>
              {HELP_RESOURCE_ORDER.map((option) => {
                const content = HELP_RESOURCE_CONTENT[option];
                const titleId = `help-resources-option-title-${option}`;
                const subtitleId = `help-resources-option-subtitle-${option}`;
                return (
                  <div
                    key={option}
                    className={mergeClasses(`help-resources-option-${option}`, styles.optionCard)}
                    role="button"
                    tabIndex={0}
                    aria-labelledby={titleId}
                    aria-describedby={subtitleId}
                    onClick={() => handleOptionClick(option)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleOptionClick(option); } }}
                  >
                    <span className={styles.optionIcon} aria-hidden>{OPTION_ICONS[option]}</span>
                    <Body1Strong id={titleId} className={styles.optionTitle}>{i18n.getString(content.title)}</Body1Strong>
                    <Caption1 id={subtitleId} className={styles.optionSubtitle}>{i18n.getString(content.subtitle)}</Caption1>
                  </div>
                );
              })}
            </div>
          </div>

          <div className={mergeClasses('help-resources-support-section', styles.supportSection)}>
            <Body1>
              <ReactTemplate
                template={i18n.getString(_TL_('Still need help? {{link}}Contact support{{/link}}'))}
                model={{
                  link: (text: string) => (
                    <Link
                      href={CONTACT_SUPPORT_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      inline
                      onClick={() => logger?.trace('HelpResourcesDrawer: Clicked Contact support link')}
                    >
                      {text}
                    </Link>
                  ),
                }}
              />
            </Body1>
            <Caption1 className={styles.supportCaption}>
              {i18n.getString(_TL_('We may ask you to verify your identity to help keep your account secure.'))}
            </Caption1>
          </div>
        </DrawerBody>

        <DrawerFooter className={styles.footer}>
          <Button className="help-resources-close-button" appearance="primary" onClick={handleClose}>
            {i18n.getString(_TL_('Close'))}
          </Button>
        </DrawerFooter>
      </StackedDrawer>

      <HelpResourcesDetailDrawer
        option={detailOption}
        onClose={handleDetailClose}
        logger={logger}
      />
    </DrawerStackProvider>
  );
};
